import { EventCallback } from "src/core/contract/EventCallback";
import { EventStorage } from "src/core/contract/EventStorage";
import { NetworkService } from "src/core/contract/NetworkService";
import { Event } from "src/core/model/Event";
import { TrackerConfiguration } from "src/core/model/TrackerConfiguration";
import { PeriodicDataScheduler } from "src/core/scheduler/PeriodicDataScheduler";
import { Logger } from "src/util/Logger";

/**
 * Service responsible for event queuing and periodic processing.
 * Mirrors Android EventService.
 */
export class EventService {
  private eventStorage: EventStorage;
  private networkService: NetworkService;
  private configuration: TrackerConfiguration;
  private scheduler: PeriodicDataScheduler | null = null;
  private consecutiveFailures = 0;
  private static readonly MAX_CONSECUTIVE_FAILURES = 5;
  private static readonly MAX_EVENT_RETRIES = 3;

  constructor(eventStorage: EventStorage, networkService: NetworkService, configuration: TrackerConfiguration) {
    this.eventStorage = eventStorage;
    this.networkService = networkService;
    this.configuration = configuration;
  }

  start(): void {
    Logger.debug("Starting EventService");
    this.scheduler = new PeriodicDataScheduler(
      () => this.processQueue(),
      this.configuration.queueProcessInterval ?? 5000,
    );
    this.scheduler.start();
  }

  stop(): void {
    Logger.debug("Stopping EventService");
    this.scheduler?.stop();
    this.scheduler = null;
  }

  enqueueEvent(event: Event): void {
    Logger.debug("Enqueuing event:", event.name);
    this.eventStorage.saveEvent(event);
  }

  async sendEventSync(event: Event, callback: EventCallback): Promise<void> {
    try {
      Logger.debug("Sending event sync:", event.name);
      const result = await this.networkService.sendEvent(event);
      if (result) {
        callback.onSuccess(result);
      }
    } catch (error) {
      Logger.error("Failed to send event:", event.name, error);
      callback.onError(error instanceof Error ? error : new Error(String(error)));
    }
  }

  private async processQueue(): Promise<void> {
    try {
      if (!this.networkService.isNetworkAvailable()) {
        Logger.debug("Network not available, skipping queue processing");
        return;
      }

      // Stop retrying after too many consecutive failures
      if (this.consecutiveFailures >= EventService.MAX_CONSECUTIVE_FAILURES) {
        Logger.debug("Too many consecutive failures, pausing queue processing");
        return;
      }

      const pendingEvents = this.eventStorage.getPendingEvents();
      if (pendingEvents.length === 0) return;

      Logger.debug("Processing queue:", pendingEvents.length, "events");

      for (const event of pendingEvents) {
        try {
          await this.networkService.sendEvent(event);
          Logger.debug("Event sent from queue:", event.name);
          this.eventStorage.removeEvent(event);
          this.consecutiveFailures = 0;
        } catch {
          this.consecutiveFailures++;
          // Increment retry count and drop events that exceeded max retries
          event.retryCount = (event.retryCount ?? 0) + 1;
          if (event.retryCount >= EventService.MAX_EVENT_RETRIES) {
            Logger.warn("Dropping event after max retries:", event.name);
            this.eventStorage.removeEvent(event);
          } else if (this.consecutiveFailures === 1) {
            // Only log the first failure, not every retry
            Logger.warn("Failed to send event, will retry:", event.name);
          }
          // Stop processing remaining events on failure
          if (this.consecutiveFailures >= EventService.MAX_CONSECUTIVE_FAILURES) {
            Logger.warn("Queue paused after", EventService.MAX_CONSECUTIVE_FAILURES, "consecutive failures");
            return;
          }
        }
      }
    } catch (error) {
      Logger.error("Critical error in processQueue:", error);
    }
  }

  /** Reset failure counter (e.g. after network recovery). */
  resetFailureCount(): void {
    this.consecutiveFailures = 0;
  }
}
